import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Check } from 'lucide-react';
import type { AppUser } from '../../types';
import type { NordicCountry } from '../../hooks/useNordicComingSoon';
import { resolveRole } from '../../utils/roleMemory';
import {
  BETA_COUNTRIES,
  type BetaCountry,
  countryAccessStatus,
  readBetaCountryPrefs,
  setBetaCountryPref,
} from '../../utils/betaCountries';
import { recordUserCountryRequest } from '../../services/firebaseService';

interface BetaCountriesPanelProps {
  user: AppUser;
}

const FLAGG: Record<BetaCountry, string> = { sweden: '🇸🇪', denmark: '🇩🇰', finland: '🇫🇮' };

/**
 * Land utenfor Norge er PÅ som standard for admin og testere. Bryterne her er
 * kun for å skru et land AV — f.eks. for å se kartet slik en vanlig bruker gjør.
 * Vanlige brukere kan trykke for å etterlyse åpning av et land.
 */
const BetaCountriesPanel: React.FC<BetaCountriesPanelProps> = ({ user }) => {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(readBetaCountryPrefs);
  const [requested, setRequested] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    if (Array.isArray(user?.requestedCountries)) {
      user.requestedCountries.forEach(c => { map[c.toLowerCase()] = true; });
    }
    return map;
  });

  const rolle = useMemo(() => resolveRole(user), [user]);
  const ownerEmail = ['jonborgentakle@gmail.com', 'dicpnorway@gmail.com']
    .includes((user?.email || '').toLowerCase());

  // Lanseringsbryteren (system/nordic_config) caches av App.tsx. Vi leser cachen
  // i stedet for å hente på nytt.
  const launched = useMemo<Partial<Record<NordicCountry, boolean>>>(() => {
    try {
      const rå = localStorage.getItem('fricamp_nordic_config');
      return rå ? JSON.parse(rå) : {};
    } catch {
      return {};
    }
  }, []);

  const navn: Record<BetaCountry, string> = {
    sweden: t('profile.beta_countries.sweden', 'Sverige'),
    denmark: t('profile.beta_countries.denmark', 'Danmark'),
    finland: t('profile.beta_countries.finland', 'Finland'),
  };

  const rader = BETA_COUNTRIES.map((land) => ({
    land,
    status: countryAccessStatus(land, launched, rolle, ownerEmail),
  }));

  const toggle = (land: BetaCountry) => {
    setPrefs(setBetaCountryPref(land, !prefs[land]));
  };

  const handleRequestCountry = (land: BetaCountry) => {
    if (!user?.uid) return;
    recordUserCountryRequest(user.uid, land).catch(() => {});
    setRequested(prev => ({ ...prev, [land]: true }));
  };

  return (
    <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl p-4 space-y-3">
      <div>
        <p className="text-xs font-black text-[var(--text-primary)]">
          {t('profile.beta_countries.title', 'Land i test')}
        </p>
        <p className="text-3xs text-[var(--text-muted)] leading-tight mt-0.5">
          {rolle.isTester || rolle.isAdmin
            ? t('profile.beta_countries.desc', 'Alt er på for deg. Skru av et land hvis du vil se kartet slik en vanlig bruker gjør.')
            : t('profile.beta_countries.locked_desc', 'Ønsker du at et land åpnes i kartet? Trykk for å etterlyse åpning.')}
        </p>
      </div>

      <div className="space-y-2">
        {rader.map(({ land, status }) => {
          const isReq = requested[land] || (user?.requestedCountries && user.requestedCountries.map(c => c.toLowerCase()).includes(land));
          return (
            <div key={land} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="text-lg">{FLAGG[land]}</span>
                <div className="min-w-0">
                  <p className="text-xs font-black text-[var(--text-primary)] truncate">{navn[land]}</p>
                  {status === 'launched' && (
                    <p className="text-3xs text-[var(--text-muted)] leading-tight">
                      {t('profile.beta_countries.launched', 'Lansert for alle')}
                    </p>
                  )}
                  {status === 'locked' && (
                    <p className="text-3xs text-[var(--text-muted)] leading-tight">
                      {isReq
                        ? t('profile.beta_countries.requested_status', 'Etterlysning registrert')
                        : t('profile.beta_countries.locked', 'Krever aktiv testerperiode')}
                    </p>
                  )}
                </div>
              </div>

              {status === 'launched' ? (
                <span className="text-3xs font-black uppercase tracking-widest text-emerald-400 shrink-0">
                  {t('profile.beta_countries.on', 'På')}
                </span>
              ) : status === 'locked' ? (
                isReq ? (
                  <span className="inline-flex items-center gap-1 text-4xs font-black uppercase tracking-widest text-emerald-400 px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 shrink-0">
                    <Check size={10} />
                    {t('profile.beta_countries.requested', 'Etterlyst')}
                  </span>
                ) : (
                  <button
                    onClick={() => handleRequestCountry(land)}
                    className="px-2.5 py-1 rounded-lg bg-violet-600/20 border border-violet-500/30 text-violet-300 hover:bg-violet-600 hover:text-white text-4xs font-black uppercase tracking-widest transition-all shrink-0 flex items-center gap-1"
                  >
                    <Lock size={10} />
                    {t('profile.beta_countries.request_btn', 'Etterlys')}
                  </button>
                )
              ) : (
                <button
                  onClick={() => toggle(land)}
                  aria-label={navn[land]}
                  aria-pressed={prefs[land]}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 shrink-0 ${
                    prefs[land] ? 'bg-emerald-500' : 'bg-stone-700'
                  }`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    prefs[land] ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BetaCountriesPanel;
